import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SectionHead } from '@/components/aurora'
import { getMyStats } from '@/api/social'

const BAR_MAX_HEIGHT = 56 // px — matches the fixed-height chart area below

/**
 * Small bar chart of the last 7 days' average exam score, mirroring the
 * mobile Home screen's "weekly activity" widget. Backed by the `weekly`
 * field of GET /api/stats/me/ (same endpoint RecentResults/StatsPage use):
 * [{ date, exams_taken, average_percent }, ...] for the trailing 7 days.
 */
export default function WeeklyActivity() {
  const { t, i18n } = useTranslation()
  const [weekly, setWeekly] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    getMyStats({ signal: controller.signal })
      .then(({ data }) => setWeekly(data.weekly ?? []))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setWeekly([]) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [])

  const hasActivity = weekly.some(d => d.exams_taken > 0)

  return (
    <div className="rounded-2xl border px-5 py-5" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <SectionHead title={t('dashboard.weeklyActivity', 'Haftalik faollik')} />

      {!loading && !hasActivity && (
        <p className="text-[12.5px] m-0" style={{ color: 'var(--ink-3)' }}>
          {t('dashboard.noWeeklyActivity', 'Bu hafta imtihon ishlanmagan')}
        </p>
      )}

      {(loading || hasActivity) && (
        <div className="flex items-end gap-2" style={{ height: BAR_MAX_HEIGHT + 24 }}>
          {(weekly.length ? weekly : Array.from({ length: 7 }, () => ({ date: null, exams_taken: 0, average_percent: 0 }))).map((day, i, arr) => {
            const isToday = i === arr.length - 1
            const pct = Math.max(0, Math.min(100, day.average_percent || 0))
            const barHeight = Math.round((pct / 100) * (BAR_MAX_HEIGHT - 6)) + 6
            const label = day.date
              ? new Date(day.date).toLocaleDateString(i18n.language, { weekday: 'short' }).slice(0, 2)
              : ''
            return (
              <div key={day.date || i} className="flex-1 flex flex-col items-center justify-end gap-1.5" style={{ height: '100%' }}>
                <div
                  title={day.exams_taken ? `${day.average_percent}%` : undefined}
                  className="w-full rounded-md transition-all"
                  style={{
                    height: barHeight,
                    background: isToday ? 'var(--coral)' : 'var(--coral-soft)',
                  }}
                />
                <span
                  className="text-[9px] font-bold uppercase"
                  style={{ color: isToday ? 'var(--coral)' : 'var(--ink-3)' }}
                >
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
